/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';
import JsonQuery from 'json-query';
import parse from 'parse-json';
import { Carousel } from 'react-responsive-carousel';
import _ from 'lodash';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import carouselStyles, { themeCarouselStyles } from './Carousel.styles';
import { sampleData } from './sampleData';

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    splitStringFrom?: string;
    dataKey?: string;
    options?: string;
    header?: Headings[];
    functionToTransform?: string;
    isTransformData?: boolean;
    useExternalIndicators?: boolean;
    renderViewPoints?: string;
    imagesLink?: string;
    imageType?: string;
    typographyClass?: string;
}

const CarouselWidget = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        dataKey,
        options,
        header,
        functionToTransform,
        isTransformData,
        useExternalIndicators,
        renderViewPoints,
        imagesLink,
        imageType,
    } = widgetProperties || props;
    const { data } = props;
    const theme = data?.themeSelected || props?.theme;
    // Options for Carousel
    let carouselOpts: any = {};

    try {
        carouselOpts = {
            ...carouselOpts,
            ...parse(
                (options as string) ||
                    // eslint-disable-next-line max-len
                    '{ "autoPlay": true, "interval": 3000, "showThumbs": false, "showStatus": false, "showArrows" : true}'
            ),
        };
    } catch (error) {
        carouselOpts = {};
    }

    const carouselData: any = dataKey ? JsonQuery(`${dataKey}`, { data }).value : sampleData;

    let transformedData = carouselData;
    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        transformedData = isTransformData
            ? commonUtils.transformDataWithGlobals(transformFunctionName, functionToTransform, {
                  carouselData,
                  _data: data,
              })
            : carouselData;
    } catch (e) {
        console.log(e);
    }

    const [currentSlide, setCurrentSlide] = useState(0);

    const next = () => {
        setCurrentSlide(() => currentSlide + 1);
    };
    const prev = () => {
        setCurrentSlide(() => currentSlide - 1);
    };
    const isDisableNextButton =
        _.isArray(transformedData) && transformedData.length - 1 === currentSlide;
    const isDisablePrevButton = currentSlide === 0;

    return (
        <div
            className={`carousel ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            css={css`
                ${!theme ? carouselStyles : themeCarouselStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles}
            `}
            data-testid={'carousel'}
        >
            <HeaderWrapper headings={header} data={data} />
            <Carousel selectedItem={currentSlide} {...carouselOpts}>
                {_.isArray(transformedData) &&
                    transformedData?.map((c: any, i: number) => {
                        return !renderViewPoints ? (
                            <div
                                className={`slide slide-${i}`}
                                key={`slide-${i}`}
                                dangerouslySetInnerHTML={{ __html: c || '' }}
                            />
                        ) : (
                            <div className={`slide slide-${i}`} key={`slide-${i}`}>
                                <img
                                    className="image"
                                    src={c.imgLink || `${imagesLink + c.id}.${imageType}`}
                                    alt={'uimage'}
                                />
                                <p className="heading">{c.header}</p>
                                <p className="text">{c.text}</p>
                                <a href={c.link} className="link" rel="noreferrer" target="_blank">
                                    <span className={`icon`}>
                                        <i className={'fas fa-chevron-right'} />
                                    </span>
                                    <span className="link-name">{c.linkName}</span>
                                </a>
                            </div>
                        );
                    })}
            </Carousel>
            {useExternalIndicators ? (
                <div className="external-indicators">
                    <button
                        disabled={isDisablePrevButton}
                        className={`${isDisablePrevButton ? 'disabled' : ''} prev`}
                        onClick={prev}
                    >
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <button
                        disabled={isDisableNextButton}
                        className={`${isDisableNextButton ? 'disabled' : ''} next`}
                        onClick={next}
                    >
                        <i className="fas fa-arrow-right"></i>
                    </button>
                </div>
            ) : (
                ''
            )}
        </div>
    );
};

export default CarouselWidget;
